'use client';

import { useState, useEffect } from 'react';

interface Props {
  modelUrl?: string;
  productName: string;
}

export function ARPreview({ modelUrl, productName }: Props) {
  const [supported, setSupported] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad/.test(ua);
    const isAndroid = /android/.test(ua);
    setSupported(isIOS || isAndroid);
  }, []);

  if (!modelUrl) return null;

  const isGLB = modelUrl.endsWith('.glb') || modelUrl.endsWith('.gltf');
  if (!isGLB) return null;

  function handleARClick() {
    if (supported) {
      const ua = navigator.userAgent.toLowerCase();
      const isIOS = /iphone|ipad/.test(ua);

      if (isIOS) {
        const usdzUrl = modelUrl!.replace(/\.glb$/, '.usdz').replace(/\.gltf$/, '.usdz');
        const a = document.createElement('a');
        a.rel = 'ar';
        a.href = usdzUrl;
        const img = document.createElement('img');
        a.appendChild(img);
        a.click();
      } else {
        const intentUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(window.location.origin + modelUrl)}&mode=ar_preferred#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;end;`;
        window.location.href = intentUrl;
      }
    } else {
      setShowQR(true);
    }
  }

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(window.location.href)}`;

  return (
    <>
      <button
        onClick={handleARClick}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-indigo-500 text-white text-sm font-medium shadow-lg hover:shadow-xl hover:scale-105 transition-all"
        title="View in Augmented Reality"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3L2 7.5V16.5L12 21L22 16.5V7.5L12 3Z" />
          <path d="M12 12L22 7.5" />
          <path d="M12 12V21" />
          <path d="M12 12L2 7.5" />
        </svg>
        {supported ? 'View in AR' : 'AR Preview'}
      </button>

      {showQR && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowQR(false)} />
          <div className="relative bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl p-6 max-w-xs text-center">
            <h3 className="text-lg font-semibold mb-1">View in AR</h3>
            <p className="text-sm text-zinc-500 mb-4">
              Scan this QR code on your phone to see {productName} in your space
            </p>
            <div className="bg-white p-3 rounded-xl inline-block mb-4">
              <img src={qrUrl} alt="QR Code for AR" width={180} height={180} className="block" />
            </div>
            <p className="text-[10px] text-zinc-400 mb-3">
              Requires ARKit (iOS) or ARCore (Android)
            </p>
            <button
              onClick={() => setShowQR(false)}
              className="w-full py-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-sm font-medium hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
